import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { message, Modal, Form, Input } from "antd";
import dayjs from "dayjs";
import CgIcon from "../common/CgIcon";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import DecimalInput from "../common/DecimalInput";
import MedicineFormModal from "../Medicines/MedicineFormModal";
import { fetchActivePharmacyMedicines } from "../common/medicineSearch";
import {
  getPharmacyMedicineApi,
  getPharmacyMedicineBatchesApi,
} from "../../../services/pharmacyMedicines.services";
import {
  addPharmacyOrderEditItemApi,
  updatePharmacyOrderEditItemApi,
  removePharmacyOrderEditItemApi,
  addPharmacyOrderItemApi,
  updatePharmacyOrderItemApi,
  deletePharmacyOrderItemApi,
  dispensePharmacyOrderApi,
} from "../../../services/pharmacyOrders.services";
import { formatRupees } from "../common/money";
import {
  computeDiscountAmount,
  effectiveRate,
  perUnitRate,
} from "../common/lineMath";
import InlinePriceHistory from "./InlinePriceHistory";
import styles from "./saleEntry.module.css";

/* Five lines is as much as the card shows; the rest scroll inside it, so the
   bill beside them is never pushed off the screen. Paired with `.seRows.cap`
   in the stylesheet, which is what holds the five. */
const LINE_CAP = 5;
const fetchPharmacyMedicines = fetchActivePharmacyMedicines;

/*
 * Whether a line is still only on this screen.
 *
 * Nothing here is written until Submit, so a line added during this visit is
 * keyed by a string of its own making until the server gives it a real id - see
 * commitDraft.
 */
const isDraftId = (id) => typeof id === "string";
const isDraftLine = (item) => isDraftId(item?.id);

/*
 * What a line's GST comes to before it has been saved.
 *
 * A saved line arrives with `pharmacy_medicine_gst_percentage` already worked
 * out, and that is the figure the bill is raised on. An unsaved one has only
 * the medicine's own record to go on, so the same sum is done here: the local
 * pair where local GST applies, the central rate and its cess where central GST
 * does. It agrees with every saved line in the data to hand.
 *
 * Worth knowing: this only ever prices the preview. The bill is generated
 * server-side from the saved lines, so a medicine taxed in some way this misses
 * can make the figure on screen disagree with the bill - it cannot bill wrongly.
 */
const gstPercentageOf = (medicine) =>
  (medicine?.is_local_gst_applicable
    ? (Number(medicine.sgst) || 0) + (Number(medicine.cgst) || 0)
    : 0) +
  (medicine?.is_central_gst_applicable
    ? (Number(medicine.igst) || 0) + (Number(medicine.cess) || 0)
    : 0);

/*
 * A medicine, shaped like the order line it is about to become.
 *
 * Every field the grid reads off a line is filled in from the medicine's own
 * record, so a row renders, prices and validates the same whether the server
 * has seen it yet or not.
 */
const draftLineFrom = (medicine, medicineId, key) => ({
  id: key,
  pharmacy_medicine: medicineId,
  pharmacy_medicine_name: medicine?.name || medicine?.medicine_name || "",
  pharmacy_medicine_drug1_name: medicine?.drug1_name || null,
  pharmacy_medicine_drug2_name: medicine?.drug2_name || null,
  pharmacy_medicine_strip_conversion: medicine?.strip_conversion ?? null,
  pharmacy_medicine_mrp: Number(medicine?.mrp) || 0,
  pharmacy_medicine_rate_a: Number(medicine?.rate_a) || 0,
  pharmacy_medicine_minimum_qty: Number(medicine?.minimum_qty) || 0,
  pharmacy_medicine_maximum_qty: Number(medicine?.maximum_qty) || 0,
  pharmacy_medicine_allow_negative_stock: Boolean(medicine?.allow_negative_stock),
  pharmacy_medicine_is_discount_applicable: medicine?.is_discount_applicable !== false,
  pharmacy_medicine_gst_percentage: gstPercentageOf(medicine),
  requested_qty: 1,
  dispense_qty: 0,
  discount: 0,
  override_rate: null,
});

const ResolveReplacementModal = ({ open, setOpen, item, onResolved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = (values) => {
    setLoading(true);
    updatePharmacyOrderItemApi(item.id, {
      pharmacy_medicine: values.pharmacy_medicine,
      is_replaced: true,
      replacement_reason: values.replacement_reason,
      /*
       * What the patient is to take of the replacement. A substitute rarely
       * comes in the same strength as what was prescribed, so the dose is
       * restated against the medicine that is actually going out rather than
       * left to be read off the original line.
       */
      replaced_dose: values.replaced_dose || undefined,
    })
      .then(() => {
        message.success("Item resolved");
        setLoading(false);
        setOpen(false);
        onResolved();
      })
      .catch((err) => {
        message.error(err?.response?.data?.message || "Failed to resolve item");
        setLoading(false);
      });
  };

  return (
    <Modal
      title="Resolve Unavailable Medicine"
      open={open}
      onCancel={() => setOpen(false)}
      onOk={() => form.submit()}
      confirmLoading={loading}
      okText="Save"
    >
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Form.Item
          label="Replacement Medicine"
          name="pharmacy_medicine"
          rules={[{ required: true, message: "Please select a replacement medicine" }]}
        >
          <AsyncSearchSelect fetcher={fetchPharmacyMedicines} placeholder="Search medicine" />
        </Form.Item>
        <Form.Item label="Dose" name="replaced_dose">
          <Input placeholder="e.g. 500mg, 1-0-1" />
        </Form.Item>
        <Form.Item
          label="Reason"
          name="replacement_reason"
          rules={[{ required: true, message: "Please enter a reason" }]}
        >
          <Input.TextArea rows={2} placeholder="e.g. Original brand out of stock" />
        </Form.Item>
      </Form>
    </Modal>
  );
};


const daysToExpiry = (batch) => {
  if (!batch?.expiry) return null;
  const days = dayjs(batch.expiry).startOf("day").diff(dayjs().startOf("day"), "day");
  return Number.isFinite(days) ? days : null;
};

const expiryTone = (days) => {
  if (days === null) return null;
  if (days < 30) return "crit";
  if (days < 90) return "warn";
  return "ok";
};

const SelectBatchModal = ({ open, setOpen, item, batches, selectedBatchId, onSelect }) => {
  const [chosen, setChosen] = useState(selectedBatchId);
  const conversion = item?.pharmacy_medicine_strip_conversion;
  const packLabel = conversion ? `per strip of ${conversion}` : "per unit";

  useEffect(() => {
    if (open) setChosen(selectedBatchId);
  }, [open, selectedBatchId]);

  const ordered = useMemo(() => {
    const rank = (batch) => {
      const days = daysToExpiry(batch);
      return days === null ? Number.POSITIVE_INFINITY : days;
    };
    return [...(batches || [])].sort((a, b) => rank(a) - rank(b));
  }, [batches]);

  return (
    <Modal
      className={styles.seDialog}
      title={
        <div style={{ minWidth: 0 }}>
          <div className={styles.seModalT}>Select Batch</div>
          <div className={styles.seModalS}>
            {[item?.pharmacy_medicine_name || item?.medicine_name, item?.pharmacy_medicine_drug1_name]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
      }
      open={open}
      centered
      width={560}
      onCancel={() => setOpen(false)}
      closeIcon={<span className={styles.iactGlyph}>✕</span>}
      footer={
        <div className={styles.seModalF}>
          <span className={styles.seModalNote}>
            <CgIcon name="clock" size={13} stroke={2.2} /> Nearest expiry first
          </span>
          <button type="button" className={`${styles.btn} ${styles.btnG}`} onClick={() => setOpen(false)}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnP}`}
            disabled={!chosen}
            onClick={() => {
              onSelect(chosen);
              setOpen(false);
            }}
          >
            Select
          </button>
        </div>
      }
    >
      {!ordered.length ? (
        <div className={styles.seNote}>No batch of this medicine has stock left.</div>
      ) : (
        <>
          <div className={styles.batchList}>
            {ordered.map((batch, index) => {
              const days = daysToExpiry(batch);
              const tone = expiryTone(days);
              const on = chosen === batch.id;
              return (
                <label
                  key={batch.id}
                  className={[styles.seBatch, on ? styles.on : null].filter(Boolean).join(" ")}
                  style={{ "--i": index }}
                >
                  <input
                    type="radio"
                    name="pharmacy-batch-pick"
                    checked={on}
                    onChange={() => setChosen(batch.id)}
                  />
                  <span className={styles.seBatchDot} />
                  <span className={styles.seBatchM}>
                    <span className={styles.seBatchTop}>
                      <b className={styles.mono}>{batch.batch_number || "No batch number"}</b>
                      {index === 0 && <span className={styles.seBatchFirst}>Use first</span>}
                    </span>
                    <i>
                      {batch.expiry ? `Expires ${dayjs(batch.expiry).format("DD MMM YYYY")}` : "No expiry on file"}
                      {" · "}
                      {batch.available_qty} in stock
                    </i>
                  </span>
                  <span className={styles.seBatchP}>
                    <b className={styles.tnum}>{formatRupees(batch.selling_price)}</b>
                    <i className={styles.tnum}>
                      MRP {formatRupees(batch.mrp)} · {packLabel}
                    </i>
                  </span>
                  <span
                    className={[styles.seBatchDays, tone ? styles[tone] : null]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <b className={styles.tnum}>{days === null ? "—" : Math.max(days, 0)}</b>
                    <i>days</i>
                  </span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
};


const displayRate = (value) => {
  const rate = Number(value);
  return Number.isFinite(rate) ? Math.round(rate * 100) / 100 : 0;
};

const toQty = (value) => {
  const units = Math.floor(Number(value));
  return Number.isFinite(units) && units > 0 ? units : 0;
};

const formatQty = (value) => {
  const n = Number(value) || 0;
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
};

const computeQty = (item, row) => {
  const conversion = item.pharmacy_medicine_strip_conversion;
  if (conversion) return (row.strip || 0) * conversion + (row.tab || 0);
  return row.tab || 0;
};

/*
 * A stock figure said the way the two boxes above it are filled in.
 *
 * The batch holds units, but the line is entered as strips and loose tablets,
 * so "only 12 left" against a medicine that comes ten to a strip leaves the
 * pharmacist doing the division. This says "a strip and 2 loose" instead.
 */
const describeStock = (item, units) => {
  const conversion = Number(item?.pharmacy_medicine_strip_conversion) || 0;
  const whole = Math.max(0, Math.floor(Number(units) || 0));
  if (!conversion || whole < conversion) return null;

  const strips = Math.floor(whole / conversion);
  const loose = whole % conversion;
  const stripPart = `${strips} strip${strips === 1 ? "" : "s"}`;
  return loose ? `${stripPart} and ${loose} loose` : stripPart;
};

const stockShortfall = (item, row, batch) => {
  if (!batch || item?.pharmacy_medicine_allow_negative_stock) return 0;
  const qty = computeQty(item, row || {});
  const available = Number(batch.available_qty ?? 0);
  return qty > available ? qty - available : 0;
};

/*
 * Why a line would be refused, worked out from what is on screen.
 *
 * One test, read in two places: by the row, which shows it under the quantity
 * as it is typed, and by Submit, which stops on it. They used to be the same
 * rules written once - at Submit - so a line below its minimum sale quantity
 * looked perfectly fine until the run was attempted, which is the worst moment
 * to learn it was never going to go out.
 *
 * Returns the sentence without the medicine's name on it; Submit puts the name
 * in front, a row does not need to be told which line it is.
 */
const quantityProblem = (item, row, batch, options = {}) => {
  const { syncRequestedQty = true, issuedQty: override } = options;
  /*
   * Submit dispenses what the row says, and falls back to what is still
   * outstanding when the row has nothing typed on it - a line whose batches had
   * not arrived yet. It passes that same figure in here, or the check would
   * test one quantity while the dispense sent another.
   */
  const issuedQty = override ?? computeQty(item, row || {});
  if (!issuedQty) return null;

  const available = Number(batch?.available_qty ?? 0);
  const capped = Boolean(batch) && !item?.pharmacy_medicine_allow_negative_stock;
  if (capped && issuedQty > available) {
    const inStrips = describeStock(item, available);
    return `only ${formatQty(available)} left${
      batch.batch_number ? ` in batch ${batch.batch_number}` : ""
    }${inStrips ? ` (${inStrips})` : ""}, cannot dispense ${formatQty(issuedQty)}.`;
  }

  /* Nothing is outstanding on a line the server has not seen. */
  const stillPending = isDraftLine(item) ? 0 : Number(item.pending_qty) || 0;
  if (stillPending && issuedQty > stillPending + 1e-6) {
    return `only ${formatQty(stillPending)} left to dispense on this line, not ${formatQty(issuedQty)}.`;
  }

  const dispenseQty = Number(item.dispense_qty) || 0;
  const totalAfter = dispenseQty + issuedQty;

  const maxQty = item.pharmacy_medicine_maximum_qty;
  if (maxQty && totalAfter > maxQty + 1e-6) {
    return `cannot dispense more than the maximum allowed quantity (${formatQty(maxQty)}).`;
  }

  /*
   * The minimum only bites once the line is finished: a part dispense on its
   * way to the minimum is fine, and only the quantity that closes the line out
   * below it is refused.
   */
  const minQty = item.pharmacy_medicine_minimum_qty;
  const effectiveRequestedQty =
    syncRequestedQty && !dispenseQty ? issuedQty : Number(item.requested_qty) || 0;
  const remainingAfter =
    effectiveRequestedQty - totalAfter - (item.cancelled_qty || 0) - (item.returned_qty || 0);
  if (minQty && remainingAfter <= 1e-6 && totalAfter < minQty - 1e-6) {
    return `${formatQty(totalAfter)} is below the minimum sale quantity of ${formatQty(minQty)}.`;
  }

  return null;
};

const capitalise = (text) =>
  typeof text === "string" && text ? text[0].toUpperCase() + text.slice(1) : text;

const MedicinesConsumablesCard = forwardRef(({ order, onRefresh, onEnsureOrder, onTotalsChange, onSelectionChange, onDraftChange, disabled, historyPatientId, historyPatientName }, ref) => {
  const [batchOptions, setBatchOptions] = useState({});
  const [rows, setRows] = useState({});
  const [discountMode, setDiscountMode] = useState("rupees");
  const [resolveItem, setResolveItem] = useState(null);
  /*
   * A line the catalogue has nothing for can be answered two ways: the
   * medicine is created as it was prescribed, or something the pharmacy does
   * have is put in its place. This is the line asking for the first.
   */
  const [addMedicineFor, setAddMedicineFor] = useState(null);
  const [batchPickerItem, setBatchPickerItem] = useState(null);
  const [draftRows, setDraftRows] = useState([]);
  const [addingKey, setAddingKey] = useState(null);
  /*
   * The entry as it stands, which is not the order as the server has it.
   *
   * Adding a medicine used to create the order and post the line straight away,
   * and every figure typed on it went up as its own PATCH - so an entry was
   * saved, and kept being re-saved, long before the pharmacist said to save it.
   * Lines added here are held in `localLines` and lines taken off a saved order
   * in `removedIds`; both are written in one go when the bill is generated, held
   * or submitted (see commitDraft).
   */
  const [localLines, setLocalLines] = useState([]);
  const [removedIds, setRemovedIds] = useState([]);
  /*
   * The line the price history panel beside this grid is showing. Selecting is
   * the only thing a click on the row body does - every control on it keeps its
   * own click, or opening a batch picker would also move the panel.
   */
  const [selectedItemId, setSelectedItemId] = useState(null);

  /* What is on the order, and what this screen has done to it. */
  const items = useMemo(() => {
    const saved = (order?.items || []).filter((item) => !removedIds.includes(item.id));
    return localLines.length ? [...saved, ...localLines] : saved;
  }, [order?.items, removedIds, localLines]);


  const displayItems = useMemo(() => {
    if (!removedIds.length) return items;
    const removed = (order?.items || [])
      .filter((item) => removedIds.includes(item.id))
      .map((item) => ({ ...item, is_removed: true }));
    if (!removed.length) return items;

    /* Back where it was, not at the end - a line that jumps to the foot of the
       list on its way out is a line nobody can find again. */
    const position = new Map((order?.items || []).map((item, index) => [item.id, index]));
    const saved = [...items.filter((item) => !isDraftLine(item)), ...removed].sort(
      (a, b) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0)
    );
    return [...saved, ...items.filter((item) => isDraftLine(item))];
  }, [items, order?.items, removedIds]);

  /*
   * Rows that have appeared since this card was mounted, so they can be dealt in
   * rather than arriving fully formed - a medicine just added, and the order's
   * own lines as they come back from the server. Whatever was already on screen
   * stays put: a refresh must not re-animate the whole list.
   */
  const seenItemsRef = useRef(null);
  const newItemIds = useMemo(() => {
    const fresh = new Set();
    if (seenItemsRef.current === null) {
      seenItemsRef.current = new Set(items.map((item) => item.id));
      return fresh;
    }
    items.forEach((item) => {
      if (!seenItemsRef.current.has(item.id)) fresh.add(item.id);
    });
    return fresh;
  }, [items]);

  useEffect(() => {
    if (seenItemsRef.current === null) return;
    items.forEach((item) => seenItemsRef.current.add(item.id));
  }, [items]);

  /*
   * Adding a medicine selects it, and removing the selected one falls back to
   * the first line left - the panel beside the grid should never be pointing at
   * a line that is no longer there.
   */
  useEffect(() => {
    if (!items.length) {
      if (selectedItemId !== null) setSelectedItemId(null);
      return;
    }
    const added = items.find((item) => newItemIds.has(item.id));
    if (added) {
      setSelectedItemId(added.id);
      return;
    }
    /*
     * Only chase a selection that pointed at a line which has since gone.
     *
     * `null` is now an answer rather than an absence - it is what closing the
     * panel leaves behind - so falling back to the first line whenever
     * nothing is selected would have reopened it on the next render, and the
     * row would not stay shut.
     */
    if (selectedItemId !== null && !items.some((item) => item.id === selectedItemId)) {
      setSelectedItemId(items[0].id);
    }
  }, [items, newItemIds]);

  /*
   * Selecting a line is a click on the row itself, and clicking the line that
   * is already open closes it again - the same switch, both ways, so the
   * panel can be put away without having to open somebody else's to do it.
   *
   * A click that landed on a control inside the row belongs to that control:
   * re-selecting under an opening dropdown or a stepper would fight with it.
   */
  const handleRowClick = (event, itemId) => {
    const target = event?.target;
    if (
      target?.closest?.(
        "input, button, a, label, textarea, .ant-select, .ant-input-number, .ant-radio-group, [role='button']"
      )
    ) {
      return;
    }
    setSelectedItemId((open) => (open === itemId ? null : itemId));
  };

 
  const ensureOrderReady = async () => {
    if (!onEnsureOrder) {
      if (order?.id) return order;
      message.warning("Please fill patient details first");
      return null;
    }
    try {
      return await onEnsureOrder();
    } catch (err) {
      return null;
    }
  };


  /*
   * One blank line at a time.
   *
   * A draft row is a line with nothing on it yet; picking a medicine turns it
   * into a real one and the row goes. Dealing another while one is still empty
   * is how four "Search and select medicine" boxes ended up stacked under each
   * other with nothing on any of them - so the button waits for the one on
   * screen to be filled in or thrown away.
   */
  const handleAddDraftRow = () => {
    if (draftRows.length) return;
    setDraftRows((prev) => [...prev, { key: `draft-${prev.length}-${Date.now()}` }]);
  };

  const discardDraftRow = (key) => setDraftRows((prev) => prev.filter((row) => row.key !== key));

  const handleSelectDraftMedicine = async (key, value) => {
    const alreadyOnOrder = items.filter(
      (item) => item.pharmacy_medicine === value
    );
    if (alreadyOnOrder.length) {
      const known = batchOptions[value];
      const batchCount = Array.isArray(known) ? known.length : null;
      const name =
        alreadyOnOrder[0].pharmacy_medicine_name ||
        alreadyOnOrder[0].medicine_name ||
        "This medicine";

      if (batchCount !== null && alreadyOnOrder.length >= Math.max(batchCount, 1)) {
        message.warning(
          batchCount <= 1
            ? `${name} is already on this order and has only one batch.`
            : `${name} is already on this order for all ${batchCount} of its batches.`
        );
        return;
      }
    }

    setAddingKey(key);
    try {
      const res = await getPharmacyMedicineApi(value);
      const medicine = res?.data;
      if (!medicine) throw new Error("No medicine returned");
      setLocalLines((prev) => [
        ...prev,
        draftLineFrom(medicine, value, `draft-line-${value}-${prev.length}-${key}`),
      ]);
      discardDraftRow(key);
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to add medicine");
    } finally {
      setAddingKey(null);
    }
  };

  /*
   * The batches a medicine can be issued from, fetched once per medicine. Kept
   * apart from seeding the row below, because the same medicine can be on the
   * order more than once - one line per batch - and the second line still needs
   * its own row even though the batches are already known.
   */
  const batchFetchRef = useRef(new Set());
  useEffect(() => {
    items.forEach((item) => {
      const medicineId = item.pharmacy_medicine;
      if (!medicineId || batchFetchRef.current.has(medicineId)) return;
      batchFetchRef.current.add(medicineId);

      getPharmacyMedicineBatchesApi(medicineId)
        .then((res) => {
          setBatchOptions((prev) => ({ ...prev, [medicineId]: res?.data || [] }));
        })
        .catch(() => {
          batchFetchRef.current.delete(medicineId);
        });
    });
  }, [items]);

 
  const seedRef = useRef({});
  useEffect(() => {
    items.forEach((item) => {
      if (!item.pharmacy_medicine || rows[item.id]) return;
      const batches = batchOptions[item.pharmacy_medicine];
      if (!batches) return;

      const packRate = batches.length
        ? batches[0].selling_price
        : item.pharmacy_medicine_rate_a || item.pharmacy_medicine_mrp || 0;

      const conversion = Number(item.pharmacy_medicine_strip_conversion) || 0;
      const dispensed = Number(item.dispense_qty) || 0;
      const wanted = dispensed > 0
        ? dispensed
        : Number(item.pending_qty ?? item.requested_qty) || 0;

      const seeded = {
        strip: conversion ? Math.floor(wanted / conversion) : 0,
        tab: conversion ? wanted % conversion : wanted,
        rate: item.override_rate ?? perUnitRate(item, packRate),
        discount: Number(item.discount) || 0,
        batch: batches.length ? batches[0].id : undefined,
      };

      seedRef.current[item.id] = seeded;
      setRows((prev) => (prev[item.id] ? prev : { ...prev, [item.id]: { ...seeded } }));
    });
  }, [items, batchOptions, rows]);

  const [rateErrors, setRateErrors] = useState({});
  /*
   * Why a quantity would not go in, said on the line it belongs to.
   *
   * This used to be a toast: it appeared over on the other side of the screen,
   * named a batch rather than a row, and was gone before the next keystroke -
   * so the box simply stopped responding and nothing on the line said why.
   */
  const [qtyErrors, setQtyErrors] = useState({});

  const setQtyError = (itemId, field, text) =>
    setQtyErrors((prev) =>
      prev[itemId]?.text === text && prev[itemId]?.field === field
        ? prev
        : { ...prev, [itemId]: { field, text } }
    );

  const clearQtyError = (itemId) =>
    setQtyErrors((prev) => {
      if (!prev[itemId]) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });

  const setRow = (itemId, patch) => {
    setRows((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));
  };

  /*
   * Settling a line.
   *
   * A line is typed into until it is ticked off, at which point it reads as
   * what it says rather than as a row of boxes - the pencil opens it again.
   * Neither mark goes anywhere near the server: the entry is written when it
   * is submitted, and the tick is only the pharmacist saying they are done
   * with that line. Undoing the whole entry is what Cancel in the footer is
   * for, as it is on the purchase screens.
   */
  const [settled, setSettled] = useState(() => new Set());

  const isRowOpen = (itemId) => !settled.has(itemId);

  const settleRow = (itemId) =>
    setSettled((prev) => new Set(prev).add(itemId));

  const openRow = (itemId) =>
    setSettled((prev) => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });

  /*
   * A line the server already holds opens settled.
   *
   * `settled` began empty, so every line on a saved order counted as still
   * being typed into: each one showed the tick that closes it and none showed
   * the pencil that opens it, which is the mark anybody looking for "edit"
   * is looking for. A line that is already on the order reads as what it says
   * until the pencil is pressed - the way the purchase screens read theirs -
   * and only a line just added is open for typing, because it has nothing in
   * it yet.
   *
   * Seeded once per line: reopening one and then refreshing the order must
   * not slam it shut again.
   */
  const seededRef = useRef(new Set());
  useEffect(() => {
    const fresh = items.filter(
      (item) => !isDraftLine(item) && !seededRef.current.has(item.id)
    );
    if (!fresh.length) return;
    fresh.forEach((item) => seededRef.current.add(item.id));
    setSettled((prev) => {
      const next = new Set(prev);
      fresh.forEach((item) => next.add(item.id));
      return next;
    });
  }, [items]);


  const setRate = (item, value, maxRate) => {
    const rate = Number(value ?? 0);
    if (Number.isNaN(rate) || rate < 0) return;

    if (maxRate != null && rate > maxRate) {
      setRateErrors((prev) => ({
        ...prev,
        [item.id]: `Cannot exceed MRP of ${formatRupees(maxRate)}`,
      }));
      return;
    }

    setRateErrors((prev) => {
      if (!prev[item.id]) return prev;
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
    setRow(item.id, { rate, rateOverridden: true });
  };

 
  const clearRateError = (itemId) =>
    setRateErrors((prev) => {
      if (!prev[itemId]) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });


  const setQty = (item, row, batch, patch) => {
    if (stockShortfall(item, { ...row, ...patch }, batch) > 0) {
      const available = Number(batch.available_qty ?? 0);
      const inStrips = describeStock(item, available);
      /* Said under the box that was being typed in, not somewhere else on the
         line - the two quantity fields are a pair and either can overrun. */
      setQtyError(
        item.id,
        Object.keys(patch)[0],
        [
          `Only ${formatQty(available)} left`,
          batch.batch_number ? ` in batch ${batch.batch_number}` : "",
          inStrips ? ` — ${inStrips}` : "",
        ].join("")
      );
      return;
    }
    clearQtyError(item.id);
    setRow(item.id, patch);
  };

  useEffect(() => {
    if (!onTotalsChange) return;

    const lines = items
      .filter((item) => item.pharmacy_medicine)
      .map((item) => {
        const row = rows[item.id] || { strip: 0, tab: 0, rate: 0, discount: 0 };
        const qty = computeQty(item, row);
        const billedRate = effectiveRate(
          item,
          qty,
          row.rate || 0,
          row.rateOverridden
        );
        return {
          id: item.id,
          name: item.pharmacy_medicine_name || item.medicine_name || "-",
          quantity: qty,
          rate: billedRate,
          lineAmount: qty * billedRate,
          lineDiscount: computeDiscountAmount(
            item,
            { ...row, rate: billedRate },
            discountMode,
            qty
          ),
          gstPercentage: item.pharmacy_medicine_gst_percentage || 0,
          /*
           * How that rate divides into heads, as the order sends it.
           *
           * The line carries one percentage, which is enough to charge but
           * not enough to print a GST summary: the summary states SGST and
           * CGST separately, and halving the rate only guesses at them. The
           * order's own split is believed where it is given.
           */
          gstSplit:
            item.pharmacy_medicine_gst_split ??
            item.pharmacy_medicine_gst_spilt ??
            item.gst_split ??
            null,
          /* Cess is a levy of its own standing beside GST, not a slab of it,
             so it is carried separately and printed under its own head. */
          cessPercentage:
            Number(item.pharmacy_medicine_cess ?? item.cess ?? 0) || 0,
        };
      })
      .filter((line) => line.quantity > 0);

    onTotalsChange({ lines });
  }, [items, rows, discountMode]);


  const reportedSelectionRef = useRef("");
  useEffect(() => {
    if (!onSelectionChange) return;
    const item = items.find((entry) => entry.id === selectedItemId);
    if (!item?.pharmacy_medicine) {
      if (reportedSelectionRef.current !== "") {
        reportedSelectionRef.current = "";
        onSelectionChange(null);
      }
      return;
    }
    const row = rows[item.id] || {};
    const qty = computeQty(item, row);
    const rate = effectiveRate(item, qty, row.rate || 0, row.rateOverridden);
    const signature = `${item.id}|${item.pharmacy_medicine}|${rate}`;
    if (reportedSelectionRef.current === signature) return;
    reportedSelectionRef.current = signature;
    onSelectionChange({
      itemId: item.id,
      medicineId: item.pharmacy_medicine,
      medicineName: item.pharmacy_medicine_name || item.medicine_name || "",
      rate,
    });
  }, [selectedItemId, items, rows]);

  const handleBatchSelect = (item, batchId) => {
    const batch = (batchOptions[item.pharmacy_medicine] || []).find((b) => b.id === batchId);
    const batchRate = batch ? perUnitRate(item, batch.selling_price) : null;
    const batchMax =
      batch?.mrp != null ? perUnitRate(item, Number(batch.mrp)) : null;
    const nextRate = batch
      ? batchMax != null
        ? Math.min(batchRate, batchMax)
        : batchRate
      : rows[item.id]?.rate;
    clearQtyError(item.id);
    setRow(item.id, { batch: batchId, rate: nextRate });
  };

  const handleDelete = (itemId) => {
    if (isDraftId(itemId)) {
      /* A line added here and taken off again goes for good, and so does what
         was typed into it - there is nothing left to print it against. */
      setLocalLines((prev) => prev.filter((line) => line.id !== itemId));
      setRows((prev) => {
        if (!prev[itemId]) return prev;
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      delete seedRef.current[itemId];
    } else {
      /* A saved line stays on screen as a record of itself, figures and all,
         and can be put back - so what was typed into it is kept exactly as it
         stood. Nothing reads it while the line is off: the totals, the batch
         fetches and Generate Bill all work from `items`, which drops it. */
      setRemovedIds((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]));
    }
    clearQtyError(itemId);
    message.success("Item removed");
  };

  /* Nothing has been deleted on the server yet - the removal is a note to
     Generate Bill - so taking the note back is all there is to undo. The row
     re-seeds itself from the order the moment it is in `items` again. */
  const handleUndoDelete = (itemId) => {
    setRemovedIds((prev) => prev.filter((id) => id !== itemId));
    message.success("Item put back");
  };

  const hasUnsavedWork =
    localLines.length > 0 ||
    removedIds.length > 0 ||
    items.some((item) => {
      const row = rows[item.id];
      const seed = seedRef.current[item.id];
      if (!row || !seed) return false;
      return (
        (Number(row.strip) || 0) !== (Number(seed.strip) || 0) ||
        (Number(row.tab) || 0) !== (Number(seed.tab) || 0) ||
        Math.abs((Number(row.rate) || 0) - (Number(seed.rate) || 0)) > 0.005 ||
        Math.abs((Number(row.discount) || 0) - (Number(seed.discount) || 0)) > 0.005 ||
        row.batch !== seed.batch
      );
    });

  const reportedDraftRef = useRef("");
  useEffect(() => {
    if (!onDraftChange) return;
    const signature = `${items.length}|${hasUnsavedWork ? 1 : 0}`;
    if (reportedDraftRef.current === signature) return;
    reportedDraftRef.current = signature;
    onDraftChange({ lines: items.length, unsaved: hasUnsavedWork });
  }, [items.length, hasUnsavedWork]);

  const commitDraft = async () => {
    const ensured = await ensureOrderReady();
    if (!ensured?.id) return null;

    const billAlreadyRaised = ensured?.status === "completed";
    for (const itemId of removedIds) {
      await (billAlreadyRaised
        ? removePharmacyOrderEditItemApi(ensured.id, itemId)
        : deletePharmacyOrderItemApi(itemId)
      ).catch(() => {});
    }
    const rekeyed = {};
    const created = [];
    for (const line of localLines) {
      const row = rows[line.id] || {};
      const requested = computeQty(line, row) || 1;
      const res = await (billAlreadyRaised
        ? addPharmacyOrderEditItemApi(ensured.id, {
            pharmacy_medicine: line.pharmacy_medicine,
            requested_qty: requested,
            batches: [{ batch: row.batch ?? null, issued_qty: requested }],
          })
        : addPharmacyOrderItemApi(ensured.id, {
            pharmacy_medicine: line.pharmacy_medicine,
            requested_qty: requested,
          }));
      const savedId = res?.data?.id;
      if (savedId == null) throw new Error("The server did not return the saved line");
      rekeyed[line.id] = savedId;
      created.push({ id: savedId, draftId: line.id, item: { ...line, id: savedId } });
    }

    /*
     * The lines just written are not new arrivals.
     *
     * They are the same lines the pharmacist has been looking at, under the
     * ids the server has given them - but to `newItemIds` they are ids never
     * seen before, which made Submit deal the whole list in again and snatch
     * the selection onto whichever of them came first. The price history
     * panel follows the selection, so it tore down and refetched two or three
     * times over while the order was being saved. They are marked seen before
     * they land, and the selection is carried across to the line's new id
     * rather than being left on an id that no longer exists.
     */
    if (seenItemsRef.current) {
      created.forEach(({ id }) => seenItemsRef.current.add(id));
    }
    if (selectedItemId != null && rekeyed[selectedItemId] != null) {
      setSelectedItemId(rekeyed[selectedItemId]);
    }
    const lines = [
      ...items
        .filter((item) => !isDraftLine(item))
        .map((item) => ({ key: item.id, id: item.id, item, isNew: false })),
      ...created.map(({ id, draftId, item }) => ({ key: draftId, id, item, isNew: true })),
    ].map((entry) => ({ ...entry, row: rows[entry.key] || {} }));

 
    const discountFor = ({ item, row }) =>
      computeDiscountAmount(item, row, discountMode, computeQty(item, row));

    /*
     * A completed order is corrected through its own edit endpoints.
     *
     * The plain item PATCH writes the line and stops there - it has no view
     * of the bill. So a rate changed on a completed order saved as
     * `override_rate` and nothing else moved: the dispense kept the price it
     * went out at and the bill kept the total it was raised for, which is
     * why the figures on screen refused to follow the rate. The edit
     * endpoints rebuild the bill after every change.
     *
     * Before an order is completed there is no bill to rebuild, and the plain
     * calls are the right ones - the server says so.
     */
    const billRaised = ensured?.status === "completed";

    const quantityMoved = (entry) => {
      const dispensed = Number(entry.item.dispense_qty) || 0;
      return Math.abs(computeQty(entry.item, entry.row) - dispensed) > 0.0001;
    };

    const needsSaving = (entry) => {
      if (!entry.item.pharmacy_medicine || !rows[entry.key]) return false;
      if (entry.isNew) return true;
      if (Math.abs((Number(entry.row.rate) || 0) - (Number(entry.item.override_rate) || 0)) > 0.005) {
        return true;
      }
      /* On a completed order the quantity can be corrected too, and the
         server moves stock by the difference. */
      if (billRaised && quantityMoved(entry)) return true;
      if (entry.item.pharmacy_medicine_is_discount_applicable === false) return false;
      return Math.abs(discountFor(entry) - (Number(entry.item.discount) || 0)) > 0.005;
    };

    await Promise.all(
      lines.filter(needsSaving).map((entry) => {
        const patch = { override_rate: Number(entry.row.rate) || 0 };
        if (entry.item.pharmacy_medicine_is_discount_applicable !== false) {
          patch.discount = discountFor(entry);
        }
        if (!billRaised) return updatePharmacyOrderItemApi(entry.id, patch);

        /* `batches` is the line's whole dispensed allocation, not a change to
           it - sent only where the quantity actually moved, so a rate
           correction does not restate the stock it went out on. */
        if (quantityMoved(entry)) {
          patch.batches = [
            {
              batch: entry.row.batch ?? null,
              issued_qty: computeQty(entry.item, entry.row),
            },
          ];
        }
        return updatePharmacyOrderEditItemApi(ensured.id, entry.id, patch);
      })
    );

    if (Object.keys(rekeyed).length) {
      setRows((prev) => {
        const next = { ...prev };
        Object.entries(rekeyed).forEach(([draftId, savedId]) => {
          next[savedId] = next[draftId];
          delete next[draftId];
        });
        return next;
      });
      Object.entries(rekeyed).forEach(([draftId, savedId]) => {
        seedRef.current[savedId] = seedRef.current[draftId];
        delete seedRef.current[draftId];
      });
    }

    /*
     * What is on screen is now what the server has, so this becomes the new
     * baseline.
     *
     * Without it the entry went on reporting unsaved work after it had been
     * saved: a line's seed still held the figures it was opened with while the
     * row held the edited ones, so going back after a successful Submit still
     * asked whether to discard.
     */
    lines.forEach(({ key, id }) => {
      const row = rows[key];
      if (row) seedRef.current[id] = { ...row };
    });

    setLocalLines([]);
    setRemovedIds([]);

    return { ...ensured, lines };
  };

  useImperativeHandle(ref, () => ({
    /*
     * Write the entry and stop there - no dispensing, no bill.
     *
     * What "Save" means on the way out: the order and its lines are recorded
     * and it stays in Pending, to be picked up and issued later.
     */
    saveDraft: async () => {
      try {
        return await commitDraft();
      } catch (err) {
        if (!err?.errorFields) {
          message.error(err?.response?.data?.message || "Failed to save this entry");
        }
        return null;
      }
    },

    saveAndDispense: async () => {
      if (!items.length) {
        message.warning("Add medicines before generating a bill");
        return null;
      }

      const pendingOf = (item) =>
        isDraftLine(item) ? computeQty(item, rows[item.id] || {}) : Number(item.pending_qty) || 0;

      const pendingItems = items.filter((item) => item.pharmacy_medicine && pendingOf(item) > 0);

      /*
       * Nothing goes out against no batch.
       *
       * A line with no batch picked used to be left out of the dispense and
       * the bill raised without it, which reads as a sale of everything on
       * the screen while one of the medicines is quietly missing from it -
       * and leaves stock that was never taken off anything. The bill waits
       * until every line has a batch to go out on, whatever the reason it has
       * none: no stock purchased in yet, or simply none chosen.
       */
      const withoutBatch = pendingItems
        .filter((item) => !rows[item.id]?.batch)
        .map((item) => item.pharmacy_medicine_name || item.medicine_name || "A medicine");

      if (withoutBatch.length) {
        message.error(
          withoutBatch.length === 1
            ? `${withoutBatch[0]} has no batch to go out on. Pick a batch, or take the line off this entry.`
            : `${withoutBatch.length} medicines have no batch to go out on. Pick a batch on each, or take them off this entry.`
        );
        return null;
      }

      const dispensableItems = pendingItems;

      const issuedQtyFor = (item) => computeQty(item, rows[item.id] || {}) || pendingOf(item);
      const syncRequestedQty = !order?.raised_by_nurse;

      const qtyViolations = [];
      dispensableItems.forEach((item) => {
        const itemRow = rows[item.id] || {};
        const chosenBatch = (batchOptions[item.pharmacy_medicine] || []).find(
          (b) => b.id === itemRow.batch
        );
        const problem = quantityProblem(item, itemRow, chosenBatch, {
          syncRequestedQty,
          issuedQty: issuedQtyFor(item),
        });
        if (problem) {
          const name = item.pharmacy_medicine_name || item.medicine_name || "Medicine";
          qtyViolations.push(`${name}: ${problem}`);
        }
      });

      if (qtyViolations.length) {
        /*
         * The lines say this themselves now, under each one. Repeating every
         * sentence as its own toast stacked a column of red over the grid and
         * covered the rows it was talking about - so one line is enough when
         * there are several, and the toast only carries the detail when there
         * is a single thing wrong and nowhere to look but at it.
         */
        message.error(
          qtyViolations.length === 1
            ? qtyViolations[0]
            : `${qtyViolations.length} medicines cannot go out as entered — each line says why.`
        );
        return null;
      }

      const saved = await commitDraft();
      if (!saved?.id) return null;
      const savedById = new Map(saved.lines.map((entry) => [entry.key, entry]));

      await Promise.all(
        dispensableItems.map((item) => {
          if (!syncRequestedQty || item.dispense_qty) return Promise.resolve();
          const entry = savedById.get(item.id);
          if (!entry || isDraftLine(item)) return Promise.resolve();
          return updatePharmacyOrderItemApi(entry.id, {
            requested_qty: issuedQtyFor(item),
          }).catch(() => {});
        })
      );

      const dispensableLines = dispensableItems
        .map((item) => {
          const entry = savedById.get(item.id);
          return {
            order_item: entry?.id ?? item.id,
            batch: rows[item.id]?.batch,
            issued_qty: issuedQtyFor(item),
          };
        })
        .filter((line) => line.issued_qty > 0);

      if (!dispensableLines.length) return { order: saved, dispensedCount: 0 };

      await dispensePharmacyOrderApi(saved.id, dispensableLines);
      return { order: saved, dispensedCount: dispensableLines.length };
    },
  }));

  /* A line already waiting for a medicine is the line to fill in next. */
  const hasBlankRow = draftRows.length > 0;

  const addMedicineButton = !disabled && (
    <button
      type="button"
      className={`${styles.btn} ${styles.btnG} ${styles.btnS}`}
      disabled={hasBlankRow}
      title={
        hasBlankRow
          ? "Pick a medicine on the empty line first, or remove it"
          : undefined
      }
      onClick={handleAddDraftRow}
    >
      <CgIcon name="plus" size={15} stroke={2.2} /> Add Medicine
    </button>
  );

  const hasRows = displayItems.length > 0 || draftRows.length > 0;

  return (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <h3 className={styles.cardTitle}>Medicines &amp; Consumables</h3>
        <span className={`${styles.cardCount} ${styles.tnum}`}>{items.length}</span>
        <div className={styles.spacer} />
        {hasRows && (
          <span className={styles.seDhead}>
            Discount in
            <span className={styles.seDmode}>
              <button
                type="button"
                className={discountMode === "percentage" ? styles.on : undefined}
                onClick={() => setDiscountMode("percentage")}
              >
                %
              </button>
              <button
                type="button"
                className={discountMode === "rupees" ? styles.on : undefined}
                onClick={() => setDiscountMode("rupees")}
              >
                ₹
              </button>
            </span>
          </span>
        )}

        {hasRows && addMedicineButton}
      </div>

      {!hasRows ? (
        <div className={styles.emp}>
          <div className={styles.empIco}>
            <CgIcon name="pill" size={26} stroke={1.6} />
          </div>
          <div className={styles.empT}>No medicines added</div>
          <div className={styles.empS}>Click &ldquo;Add Medicine&rdquo; to begin billing.</div>
          <div style={{ marginTop: 16 }}>{addMedicineButton}</div>
        </div>
      ) : (
        <div className={styles.seScroll}>
          <div className={`${styles.prow} ${styles.seCols} ${styles.phdr}`}>
            <div>Medicine</div>
            <div>Batch</div>
            <div className={styles.end}>Strip</div>
            <div className={styles.end}>Tab</div>
            <div className={styles.end}>Total Qty</div>
            <div className={styles.end}>Rate</div>
            <div className={styles.end}>Discount</div>
            <div className={styles.end}>GST</div>
            <div className={styles.end}>Amount</div>
            <div className={styles.end}>Action</div>
          </div>

          <div
            className={`${styles.seRows} ${
              displayItems.length > LINE_CAP ? styles.cap : ""
            }`}
          >
            {draftRows.map((draft) => (
              <div
                key={draft.key}
                className={`${styles.prow} ${styles.seCols} ${styles.draft} ${styles.seRowNew}`}
              >
                <div className={styles.seDraft}>
                  <AsyncSearchSelect
                    fetcher={fetchPharmacyMedicines}
                    placeholder="Search and select medicine"
                    value={null}
                    disabled={addingKey === draft.key}
                    onChange={(value) => handleSelectDraftMedicine(draft.key, value)}
                  />
                </div>
                <div className={styles.end}>
                  <span className={styles.seActs}>
                    <button
                      type="button"
                      aria-label="Discard row"
                      title="Discard line"
                      className={`${styles.iact} ${styles.iactDanger}`}
                      onClick={() => discardDraftRow(draft.key)}
                    >
                      <CgIcon name="trash" size={15} stroke={2} />
                    </button>
                  </span>
                </div>
              </div>
            ))}

            {displayItems.map((item) => {
              const row = rows[item.id] || { strip: 0, tab: 0, rate: 0, discount: 0 };
              const hasStripConversion = Boolean(item.pharmacy_medicine_strip_conversion);
              const qty = computeQty(item, row);
              const billedRate = effectiveRate(item, qty, row.rate || 0, row.rateOverridden);
              const discountAmount = computeDiscountAmount(
                item,
                { ...row, rate: billedRate },
                discountMode,
                qty
              );
              const taxable = Math.max(qty * billedRate - discountAmount, 0);
              const gstPercentage = item.pharmacy_medicine_gst_percentage || 0;
              const gstAmount = taxable * (gstPercentage / 100);
              const amount = taxable + gstAmount;
              const alreadyDispensed = Boolean(item.dispense_qty);
              const isUnresolved = !item.pharmacy_medicine;
              const rowOpen = isRowOpen(item.id);
              /*
               * Whether this row's boxes take anything.
               *
               * Being dispensed no longer locks a line on its own: stock
               * having moved is exactly the case the 24-hour window exists
               * for, and the purchase screens let a completed document be
               * corrected inside it. `disabled` carries that rule down from
               * the screen; this only adds what is true of the row itself.
               */
              const rowDisabled = disabled || isUnresolved || !rowOpen;
              const discountDisabled = item.pharmacy_medicine_is_discount_applicable === false;
              const batchesLoaded = Boolean(batchOptions[item.pharmacy_medicine]);
              const availableBatches = batchOptions[item.pharmacy_medicine] || [];
              const hasNoBatches = batchesLoaded && availableBatches.length === 0;
              const selectedBatch = availableBatches.find((b) => b.id === row.batch);
              /*
               * What Submit would refuse this line for, said now. Out of stock
               * is its own case: there is no quantity to test, the medicine
               * simply cannot be supplied from this counter today.
               */
              const outOfStock = hasNoBatches && !item.pharmacy_medicine_allow_negative_stock;
              /*
               * Only a line that still has something to give can be wrong
               * about how much it is giving.
               *
               * The same test Submit filters on. Without it a line that had
               * already gone out in full was still checked, and because its
               * boxes show what was dispensed rather than what is about to be,
               * the quantity got counted twice - six dispensed plus six
               * "about to be" tripped a maximum of six on a finished line.
               */
              const stillToGive = isDraftLine(item)
                ? computeQty(item, row)
                : Number(item.pending_qty) || 0;
              const rowProblem =
                stillToGive <= 0
                  ? null
                  : outOfStock
                    ? "No batch of this medicine has stock left."
                    : quantityProblem(item, row, selectedBatch, {
                        syncRequestedQty: !order?.raised_by_nurse,
                      });
              const rateError = rateErrors[item.id];
              const maxRate =
                selectedBatch?.mrp != null ? perUnitRate(item, Number(selectedBatch.mrp)) : null;
              const stockCap =
                selectedBatch && !item.pharmacy_medicine_allow_negative_stock
                  ? Number(selectedBatch.available_qty ?? 0)
                  : Infinity;
              const conversion = item.pharmacy_medicine_strip_conversion || 0;
              const stripMax = Number.isFinite(stockCap)
                ? conversion
                  ? Math.max(0, Math.floor((stockCap - (row.tab || 0)) / conversion))
                  : 0
                : Infinity;
              const tabMax = Number.isFinite(stockCap)
                ? Math.max(0, stockCap - (row.strip || 0) * conversion)
                : Infinity;

              const selected = selectedItemId === item.id;
              const daysLeft = daysToExpiry(selectedBatch);
              const batchTone = expiryTone(daysLeft);
              /* How many a strip holds - the figure the Strip and Tab boxes
                 are useless without, so it reads directly under them. */
              const perStrip = Number(item.pharmacy_medicine_strip_conversion) || 0;

              const medicineName = item.pharmacy_medicine_name || item.medicine_name || "-";
              const salts = [item.pharmacy_medicine_drug1_name, item.pharmacy_medicine_drug2_name]
                .filter(Boolean)
                .join(" · ");

             
              if (item.is_removed) {
                const askedFor = Number(item.requested_qty) || 0;
                const wasDispensed = Number(item.dispense_qty) || 0;
                return (
                  <div
                    key={item.id}
                    className={`${styles.prow} ${styles.seCols} ${styles.seRowRemoved}`}
                  >
                    <div>
                      <span className={styles.seMedName} title={medicineName}>
                        {medicineName}
                      </span>
                      {salts && (
                        <span className={styles.seMedSub} title={salts}>
                          {salts}
                        </span>
                      )}
                      <span className={styles.seMedTags}>
                        <span className={`${styles.seTag} ${styles.seTagDanger}`}>
                          Removed
                        </span>
                        {/* What the ward asked for, so the line still says
                            what is not being supplied against it. */}
                        {askedFor > 0 && (
                          <span className={styles.seTag}>
                            Asked {formatQty(askedFor)}
                          </span>
                        )}
                        {/* Stock that has already left the shelf against this
                            line. Taking the line off does not put it back, and
                            a row that said nothing about it read as though it
                            had never gone out. */}
                        {wasDispensed > 0 && (
                          <span className={`${styles.seTag} ${styles.seTagWarn}`}>
                            {formatQty(wasDispensed)} already dispensed
                          </span>
                        )}
                      </span>
                    </div>

                    <div>
                      <span className={styles.seV}>
                        {selectedBatch?.batch_number || "-"}
                      </span>
                      {selectedBatch?.expiry && (
                        <span className={styles.seMedSub}>
                          {`exp ${dayjs(selectedBatch.expiry).format("DD MMM YYYY")}`}
                        </span>
                      )}
                    </div>

                    <div>
                      <span className={styles.seV}>{row.strip ?? 0}</span>
                    </div>
                    <div>
                      <span className={styles.seV}>{row.tab ?? 0}</span>
                    </div>
                    <div className={styles.end}>
                      <span className={styles.seV}>
                        {qty ? `${formatQty(qty)} units` : "-"}
                      </span>
                    </div>
                    <div className={styles.end}>
                      <span className={styles.seV}>
                        {isUnresolved ? "-" : formatRupees(billedRate)}
                      </span>
                    </div>
                    <div className={styles.end}>
                      <span className={styles.seV}>{row.discount ?? 0}</span>
                    </div>
                    <div
                      className={`${styles.end} ${styles.seMoney} ${styles.tnum} ${styles.dim}`}
                    >
                      <span className={styles.seV}>
                        {isUnresolved ? (
                          "-"
                        ) : (
                          <>
                            {gstPercentage}%
                            <i className={styles.seGstAmt}>{formatRupees(gstAmount)}</i>
                          </>
                        )}
                      </span>
                    </div>
                    <div
                      className={`${styles.end} ${styles.seMoney} ${styles.tnum}`}
                      style={{ fontWeight: 600 }}
                      title={isUnresolved ? undefined : formatRupees(amount)}
                    >
                      <span className={styles.seV}>
                        {isUnresolved ? "-" : formatRupees(amount)}
                      </span>
                    </div>

                    <div className={styles.end}>
                      <span className={styles.seActs}>
                        {!disabled && (
                          <button
                            type="button"
                            aria-label="Put this item back"
                            title="Put this item back"
                            className={styles.iact}
                            onClick={() => handleUndoDelete(item.id)}
                          >
                            <CgIcon name="undo" size={15} stroke={2} />
                          </button>
                        )}
                      </span>
                    </div>
                  </div>
                );
              }

              return (
                /*
                  * The row and the ledger that opens under it are one entry.
                  * The panel is the row's sibling rather than its child, so a
                  * click inside the ledger - the fold toggle, a drag over the
                  * figures - does not travel up to the row and close the thing
                  * that was just opened.
                  */
                <React.Fragment key={item.id}>
                <div
                  onClick={(event) => handleRowClick(event, item.id)}
                  aria-selected={selected}
                  className={[
                    styles.prow,
                    styles.seCols,
                    selected ? styles.seRowOn : null,
                    newItemIds.has(item.id) ? styles.seRowNew : null,
                    isUnresolved ? styles.seRowFlagged : null,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div>
                    <span className={styles.seMedName} title={medicineName}>
                      {medicineName}
                    </span>
                    {salts && (
                      <span className={styles.seMedSub} title={salts}>
                        {salts}
                      </span>
                    )}
                    {item.dispense_qty > 0 && (
                      <span className={styles.seMedFlow}>
                        {`${formatQty(item.dispense_qty)} of ${formatQty(
                          item.requested_qty
                        )} dispensed`}
                      </span>
                    )}
                    {(item.is_replaced || isUnresolved) && (
                      <span className={styles.seMedTags}>
                        {item.is_replaced && (
                          /* The dose the substitute is to be taken at, where
                             one was set when it was resolved - a replacement
                             rarely matches the strength it stands in for. */
                          <span
                            className={`${styles.seTag} ${styles.seTagWarn}`}
                            title={item.replacement_reason || undefined}
                          >
                            Replaced{item.replaced_dose ? ` · ${item.replaced_dose}` : ""}
                          </span>
                        )}
                        {/*
                          * A medicine the pharmacy's catalogue has nothing
                          * for: what is wrong is said beside the name in the
                          * same words the purchase entry uses, and the way to
                          * put it right sits next to it as a button rather
                          * than being folded into the sentence.
                          */}
                        {isUnresolved && (
                          <span className={styles.seFlagWrap}>
                            <span className={styles.seFlag}>
                              <CgIcon name="info" size={13} stroke={2.1} />
                              Not in catalogue
                            </span>
                            {!disabled && (
                              <span className={styles.seFlagBtns}>
                                <button
                                  type="button"
                                  className={styles.seFlagBtn}
                                  onClick={() => setAddMedicineFor(item)}
                                >
                                  <CgIcon name="plus" size={13} stroke={2.2} />
                                  Add Medicine
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.seFlagBtn} ${styles.seFlagBtnAlt}`}
                                  onClick={() => setResolveItem(item)}
                                >
                                  <CgIcon name="swap" size={13} stroke={2.1} />
                                  Replace Medicine
                                </button>
                              </span>
                            )}
                          </span>
                        )}
                      </span>
                    )}
                  </div>

                  <div>
                    {isUnresolved ? (
                      <span className={styles.seV}>-</span>
                    ) : /*
                      * A line that has gone out names the batch it went out
                      * on and stops there. The quantity and the rate can be
                      * corrected inside the window; which batch the stock
                      * physically left is not a correction, it is a different
                      * movement.
                      */
                    alreadyDispensed ? (
                      <span className={styles.seV}>
                        {selectedBatch?.expiry
                          ? `Exp ${dayjs(selectedBatch.expiry).format("MMM YYYY")}`
                          : "-"}
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          disabled={disabled}
                          title={
                            hasNoBatches
                              ? "No batch of this medicine has stock left"
                              : "Choose a batch"
                          }
                          onClick={() => setBatchPickerItem(item)}
                          className={[
                            styles.seBatchBtn,
                            selectedBatch ? null : styles.none,
                            batchTone === "crit" ? styles.crit : null,
                            batchTone === "warn" ? styles.warn : null,
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          <span>
                            {selectedBatch
                              ? selectedBatch.batch_number || "Batch"
                              : "Select batch"}
                          </span>
                          <CgIcon name="chev" size={13} stroke={2.4} />
                        </button>
                        {selectedBatch && (
                          <span
                            className={styles.seMedSub}
                            title={`${selectedBatch.available_qty} in stock`}
                          >
                            {selectedBatch.expiry
                              ? `exp ${dayjs(selectedBatch.expiry).format("DD MMM YYYY")}`
                              : "no expiry"}
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  <div>
                    {rowOpen ? (
                      <input
                        className={`${styles.fi} ${styles.sm} ${styles.num}`}
                        inputMode="numeric"
                        aria-label="Strips"
                        max={Number.isFinite(stripMax) ? stripMax : undefined}
                        value={row.strip ?? 0}
                        disabled={rowDisabled || !hasStripConversion}
                        onChange={(e) =>
                          setQty(item, row, selectedBatch, {
                            strip: toQty(e.target.value),
                          })
                        }
                      />
                    ) : (
                      <span className={styles.seV}>{row.strip ?? 0}</span>
                    )}
                    {/* What one strip holds, under the box that counts them -
                        the figure the two boxes are useless without. */}
                    {!isUnresolved && perStrip ? (
                      <span
                        className={styles.seMedSub}
                        title={`1 strip = ${formatQty(perStrip)} tabs`}
                      >
                        {`x${formatQty(perStrip)} tabs`}
                      </span>
                    ) : null}
                  </div>

                  <div>
                    {rowOpen ? (
                      <input
                        className={`${styles.fi} ${styles.sm} ${styles.num}`}
                        inputMode="numeric"
                        aria-label="Loose units"
                        max={Number.isFinite(tabMax) ? tabMax : undefined}
                        value={row.tab ?? 0}
                        disabled={rowDisabled}
                        onChange={(e) =>
                          setQty(item, row, selectedBatch, { tab: toQty(e.target.value) })
                        }
                      />
                    ) : (
                      <span className={styles.seV}>{row.tab ?? 0}</span>
                    )}
                    {/*
                      * Two ways a quantity can be wrong, and both land here: a
                      * keystroke that would have gone past the batch, and a
                      * batch swapped underneath a quantity that was already
                      * fine. The mark is the module's warning triangle, so it
                      * reads as a refusal rather than a note.
                      */}
                  </div>

                  {/*
                    * What is actually going out, in units.
                    *
                    * The two boxes beside it are how it was entered; this is
                    * what it comes to. The sum that got there reads underneath
                    * so the figure can be checked at a glance rather than
                    * taken on trust.
                    */}
                  <div
                    className={`${styles.end} ${styles.seMoney} ${styles.tnum}`}
                    style={{ fontWeight: 600 }}
                  >
                    <span className={styles.seV}>
                      {isUnresolved || !qty ? (
                        "-"
                      ) : (
                        <>
                          {formatQty(qty)}
                          <i className={styles.seTotalUnit}>
                            {qty === 1 ? "unit" : "units"}
                          </i>
                        </>
                      )}
                    </span>
                  </div>

                  <div className={styles.end}>
                    {isUnresolved ? (
                      <span className={styles.seV}>-</span>
                    ) : (
                      <>
                        {rowOpen ? (
                          <input
                            className={`${styles.fi} ${styles.sm} ${styles.num}`}
                            inputMode="decimal"
                            aria-label="Rate"
                            value={displayRate(billedRate)}
                            disabled={rowDisabled}
                            onChange={(e) => setRate(item, e.target.value, maxRate)}
                            onBlur={() => clearRateError(item.id)}
                            style={rateError ? { borderColor: "#b42318" } : undefined}
                          />
                        ) : (
                          <span className={styles.seV}>{formatRupees(billedRate)}</span>
                        )}
                        {rateError && <span className={styles.seErr}>{rateError}</span>}
                        {/* What the figure above is a rate for. The boxes are
                            counted in strips and tabs, so the rate beside them
                            has to say which of the two it is priced in. */}
                        <span className={styles.seMedSub}>per tab</span>
                      </>
                    )}
                  </div>

                  <div className={styles.end}>
                    {rowOpen ? (
                      <DecimalInput
                        className={`${styles.fi} ${styles.sm} ${styles.num}`}
                        aria-label="Discount"
                        title={
                          discountDisabled
                            ? "Discount is not applicable for this medicine"
                            : undefined
                        }
                        value={row.discount ?? 0}
                        disabled={rowDisabled || discountDisabled}
                        onChange={(next) => setRow(item.id, { discount: next })}
                      />
                    ) : (
                      <span className={styles.seV}>{row.discount ?? 0}</span>
                    )}
                  </div>

                  <div
                    className={`${styles.end} ${styles.seMoney} ${styles.tnum} ${styles.dim}`}
                  >
                    <span className={styles.seV}>
                      {isUnresolved ? (
                        "-"
                      ) : (
                        <>
                          {gstPercentage}%
                          <i className={styles.seGstAmt}>{formatRupees(gstAmount)}</i>
                        </>
                      )}
                    </span>
                  </div>

                  <div
                    className={`${styles.end} ${styles.seMoney} ${styles.tnum}`}
                    style={{ fontWeight: 600 }}
                    /* The column is sized for a figure in the crores; a title
                       keeps anything past that readable rather than lost. */
                    title={isUnresolved ? undefined : formatRupees(amount)}
                  >
                    <span className={styles.seV}>{isUnresolved ? "-" : formatRupees(amount)}</span>
                  </div>

                  <div className={styles.end}>
                    <span className={styles.seActs}>
                      {/*
                        * The line is typed into until it is ticked off, which
                        * only settles it on screen - nothing is written until
                        * the entry is submitted. The cross puts the line back
                        * as it was, and the pencil opens it again.
                        */}
                      {/* The same three marks the purchase screens use, so a
                          line is ticked off, undone and reopened the same way
                          wherever it is being typed. */}
                      {/*
                        * A line already on the order is read until the pencil
                        * opens it, and ticked off when it is done with. A line
                        * being added for the first time has neither: there is
                        * nothing to reopen and nothing to settle, only the
                        * bin if it was a mistake.
                        */}
                      {!disabled && !isUnresolved && !isDraftLine(item) && (
                        rowOpen ? (
                          <button
                            type="button"
                            aria-label="Keep this line"
                            title="Keep this line"
                            className={styles.iact}
                            onClick={() => settleRow(item.id)}
                          >
                            <CgIcon name="check" size={15} stroke={2.2} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            aria-label="Edit this line"
                            title="Edit this line"
                            className={styles.iact}
                            onClick={() => openRow(item.id)}
                          >
                            <CgIcon name="edit" size={15} stroke={2} />
                          </button>
                        )
                      )}
                      {!disabled && (
                        <button
                          type="button"
                          aria-label="Remove item"
                          title="Remove line"
                          className={`${styles.iact} ${styles.iactDanger}`}
                          onClick={() => handleDelete(item.id)}
                        >
                          <CgIcon name="trash" size={15} stroke={2} />
                        </button>
                      )}
                    </span>
                  </div>

                  {/*
                    * The complaint gets the width of the row.
                    *
                    * It used to sit under whichever quantity box raised it, and
                    * those columns are 46px wide - a sentence in there broke to
                    * two or three words a line and pushed the row open. A
                    * refused keystroke leads, since it is about what was just
                    * typed; otherwise the line says what would stop this at
                    * Submit.
                    */}
                  {(qtyErrors[item.id] || rowProblem) && (
                    <div className={styles.seRowWarn}>
                      <CgIcon name="alert" size={13} stroke={2.1} />
                      <span>
                        {qtyErrors[item.id]
                          ? qtyErrors[item.id].text
                          : capitalise(rowProblem)}
                      </span>
                    </div>
                  )}
                </div>

                {selected && item.pharmacy_medicine ? (
                  <InlinePriceHistory
                    patientId={historyPatientId}
                    patientName={historyPatientName}
                    medicineId={item.pharmacy_medicine}
                    medicineName={medicineName}
                    currentRate={billedRate}
                    orderId={order?.id}
                  />
                ) : null}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {addMedicineFor && (
        <MedicineFormModal
          open={Boolean(addMedicineFor)}
          setOpen={() => setAddMedicineFor(null)}
          /* The product opens on the name the prescription used - there is no
             sense in typing it a second time. */
          editData={{
            name:
              addMedicineFor.medicine_name ||
              addMedicineFor.name ||
              addMedicineFor.requested_medicine_name ||
              "",
          }}
          /* Created, and put on the line that asked for it - the line is not
             a replacement, it is the same medicine the catalogue now has. */
          refreshPage={(created) => {
            if (!created?.id) return;
            const line = addMedicineFor;
            setAddMedicineFor(null);
            updatePharmacyOrderItemApi(line.id, { pharmacy_medicine: created.id })
              .then(() => {
                message.success(`${created.name} added to the catalogue`);
                onRefresh();
              })
              .catch((err) =>
                message.error(
                  err?.response?.data?.message ||
                    "The medicine was created but could not be put on this line"
                )
              );
          }}
        />
      )}

      {resolveItem && (
        <ResolveReplacementModal
          open={Boolean(resolveItem)}
          setOpen={() => setResolveItem(null)}
          item={resolveItem}
          onResolved={onRefresh}
        />
      )}

      {batchPickerItem && (
        <SelectBatchModal
          open={Boolean(batchPickerItem)}
          setOpen={() => setBatchPickerItem(null)}
          item={batchPickerItem}
          batches={batchOptions[batchPickerItem.pharmacy_medicine] || []}
          selectedBatchId={rows[batchPickerItem.id]?.batch}
          onSelect={(batchId) => handleBatchSelect(batchPickerItem, batchId)}
        />
      )}
    </section>
  );
});

export default MedicinesConsumablesCard;
